import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Loader2, Plus, Search, Book, Trash2, Edit2, Upload, FileText } from 'lucide-react';
import { getDegreeWork, createContent, updateContent, deleteContent, uploadAsset } from '../../services/api';
import { toast } from 'sonner';

const AdminDegreeWorkPage = () => {
    const [degreeWork, setDegreeWork] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Form dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        content: '',
        url: ''
    });

    useEffect(() => {
        fetchDegreeWork();
    }, []);

    const fetchDegreeWork = async () => {
        setLoading(true);
        try {
            const response = await getDegreeWork();
            setDegreeWork(response.data.degreeWork || []);
        } catch (error) {
            console.error('Failed to fetch degree work:', error);
            toast.error('Failed to load degree work materials');
        } finally {
            setLoading(false);
        }
    };

    const filteredDegreeWork = degreeWork.filter((item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleOpenAdd = () => {
        setEditingItem(null);
        setFormData({
            title: '',
            description: '',
            content: '',
            url: ''
        });
        setDialogOpen(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setFormData({
            title: item.title,
            description: item.description || '',
            content: item.content || '',
            url: item.url || ''
        });
        setDialogOpen(true);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            toast.error('Please upload a PDF file');
            return;
        }

        setUploading(true);
        try {
            const response = await uploadAsset(file);
            setFormData((prev) => ({ ...prev, url: response.data.url }));
            toast.success('File uploaded successfully');
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error('Failed to upload file');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            const payload = {
                type: 'DEGREE_WORK',
                ...formData
            };

            if (editingItem) {
                await updateContent(editingItem.PK, editingItem.SK, payload);
                toast.success('Degree material updated successfully');
            } else {
                await createContent(payload);
                toast.success('Degree material created successfully');
            }
            setDialogOpen(false);
            fetchDegreeWork();
        } catch (error) {
            console.error('Failed to save degree work:', error);
            toast.error('Failed to save degree material');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Are you sure you want to delete "${item.title}"?`)) return;

        try {
            await deleteContent(item.PK, item.SK);
            toast.success('Degree material deleted successfully');
            fetchDegreeWork();
        } catch (error) {
            console.error('Failed to delete degree material:', error);
            toast.error('Failed to delete degree material');
        }
    };

    if (loading && degreeWork.length === 0) {
        return (
            <div className="p-6 md:p-12 flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-12" data-testid="admin-degree-work-page">
            <div className="max-w-5xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-gray-900">Degree Work</h1>
                        <p className="text-gray-600 mt-1">Manage ritual materials and degree information.</p>
                    </div>
                    <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleOpenAdd} data-testid="add-degree-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Material
                    </Button>
                </div>

                {/* Search */}
                <Card className="border-gray-100 mb-6">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Search materials by title or description..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                                data-testid="degree-search-input"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card className="border-gray-100">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead>Title</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredDegreeWork.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                                            <Book className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                            No degree materials found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredDegreeWork.map((item) => (
                                        <TableRow key={item.id} data-testid={`degree-row-${item.id}`}>
                                            <TableCell className="font-medium">{item.title}</TableCell>
                                            <TableCell className="text-gray-500">{item.description}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(item)}>
                                                        <Edit2 className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item)} className="text-red-600 hover:text-red-700">
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingItem ? 'Edit Material' : 'Add New Material'}</DialogTitle>
                        <DialogDescription>
                            Enter the details for the degree work material.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Title</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g. First Degree Ritual"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description">Short Description / Subtitle</Label>
                                <Input
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="e.g. Entered Apprentice"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="pdf-upload">Upload PDF Document</Label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Input
                                            id="url"
                                            value={formData.url}
                                            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                            placeholder="https://example.com/ritual.pdf"
                                            className="pr-10"
                                        />
                                        {formData.url && !uploading && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <FileText className="h-4 w-4 text-emerald-600" />
                                            </div>
                                        )}
                                    </div>
                                    <Label
                                        htmlFor="file-upload"
                                        className={`flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {uploading ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                            <Upload className="h-4 w-4 mr-2 text-amber-600" />
                                        )}
                                        {uploading ? 'Uploading...' : 'Upload'}
                                    </Label>
                                    <input
                                        id="file-upload"
                                        type="file"
                                        className="hidden"
                                        accept=".pdf"
                                        onChange={handleFileUpload}
                                        disabled={uploading}
                                    />
                                </div>
                                <p className="text-xs text-gray-500">
                                    Upload a PDF from your computer or paste a direct URL.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="content">Full Content (Optional, HTML supported)</Label>
                                <Textarea
                                    id="content"
                                    value={formData.content}
                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                    placeholder="Enter the material content here..."
                                    rows={8}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={formLoading}>
                                {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {editingItem ? 'Update Material' : 'Save Material'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDegreeWorkPage;
